import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { appRoutes, locationRoutes } from '@/lib/route-map';
import {
  clearLegacyActive,
  readActiveTenant,
  readLegacyActive,
  writeActiveTenant,
} from '@/lib/active-tenant';
import { resolveApp, type DiscoveryResponse } from '@/lib/active-tenant-resolve';
import { resolveLegacyLocationId } from '@/components/legacy-redirect';
import { knownGbpConnectError } from '@/lib/gbp-connect-errors';
import { readArrival, consumeArrival } from '@/lib/arrival-context';
import { stashAttachedBusiness } from '@/lib/attach-context';
import type { PublicBusinessLike } from '@/lib/attach-context';

// Simple, honest visible text for the 4 claim-flow error codes the server
// redirects here with (apps/dashboard/src/routes/claim-finish.ts, all four
// `c.redirect('/app?error=...', 302)`). Full styling lands in Task 12 —
// this is the placeholder that stops a failed claim from being silently
// swallowed by the smart redirect below.
const CLAIM_ERROR_MESSAGES: Record<string, string> = {
  claim_not_found: "We couldn't find that demo to claim. The link may be wrong or expired.",
  demo_already_claimed: 'That demo has already been claimed.',
  demo_unavailable: "That demo isn't available to claim right now.",
  claim_email_mismatch:
    "That claim link was sent to a different email address than the one you're signed in with.",
};

// WP2 Task 8: the honest, human-facing text for each `POST
// /api/drafts/:id/attach` failure code (apps/dashboard/src/routes/api/drafts.ts).
// An error code this map doesn't recognise (a future server error we
// haven't accounted for here) falls back to DRAFT_ATTACH_FALLBACK_MESSAGE
// rather than ever showing a raw error token.
const DRAFT_ATTACH_ERROR_MESSAGES: Record<string, string> = {
  not_found: "We couldn't find that preview — it may have expired.",
  draft_already_claimed: 'That preview has already been claimed by another account.',
  draft_not_ready: "That preview isn't ready yet — give it a few more seconds and try again.",
  draft_attach_failed: "Something went wrong getting your preview ready. Please try again.",
};
const DRAFT_ATTACH_FALLBACK_MESSAGE = "We couldn't get your preview ready. Please try again.";

/**
 * Smart `/app` landing (WP1 Task 5): fetches the org's sites and forwards
 * straight into the panel shell — `locationRoutes.overview(businessId, lastActive ?? sites[0])`
 * when there's at least one site, `/app/new` when there are none — instead
 * of showing a site picker. A recognised `?error=` from either the
 * demo-claim flow or the GBP-connect callback takes over the whole screen
 * instead of redirecting, so the message is actually seen — `/app` is a
 * documented landing target for both (the old SitesList index rendered the
 * GBP set via the same `knownGbpConnectError` helper; that must not regress
 * just because the index component changed).
 *
 * WP2 Task 8: `/app` is also THE arrival point for `?draft=<id>`. The
 * marketing "make it yours" CTA rides the draft id through
 * `/login?redirect_after=%2Fapp%3Fdraft%3D{id}` (docs/superpowers/plans/
 * 2026-08-16-wp2-preview-engine.md:118-122); index.ts's login-redirect fix
 * (Task 7) keeps that query string alive through the OAuth round-trip, so a
 * fresh signup lands HERE holding it — never on a specific site's overview
 * route, which doesn't exist yet for a brand-new org. `readArrival()` is
 * read once via a lazy initializer (same run-once shape as ClaimedBanner's
 * own `visible`); when a draft id is present this component calls `POST
 * /api/drafts/:id/attach` BEFORE the existing /api/sites redirect logic
 * runs, shows the same "we're getting your preview ready" interim copy
 * overview.tsx's WP1 stub used, and routes straight to the new site's
 * ONBOARDING WIZARD on success (Task 12: this is the swap WP2 Task 8's own
 * header comment announced — "WP3 will retarget this to the onboarding
 * wizard instead"). Before navigating, the attach response's `business` is
 * stashed via `stashAttachedBusiness(res.locationId, res.business)` so the
 * wizard's Foundation step (foundation-step.tsx) can pre-fill the NAP
 * fields the owner already confirmed during the demo, instead of asking
 * again — the stash is scoped to `res.locationId`, so it can only ever seed
 * THIS attach's own new site. `consumeArrival()` fires exactly once, on
 * EITHER outcome — a refresh must never re-attach and must never loop on a
 * dead draft.
 */
export function AppIndex() {
  const [searchParams] = useSearchParams();
  const errorCode = searchParams.get('error');
  const claimError = errorCode ? (CLAIM_ERROR_MESSAGES[errorCode] ?? null) : null;
  const gbpError = !claimError && errorCode ? knownGbpConnectError(errorCode) : null;

  const [arrival] = useState(() => readArrival());
  const [noLocation, setNoLocation] = useState(false);
  const [attachedSiteId, setAttachedSiteId] = useState<string | null>(null);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [attachedTarget, setAttachedTarget] = useState<string | null>(null);

  useEffect(() => {
    if (!arrival.draft) return;
    let cancelled = false;
    api
      .post<{ locationId: string; slug: string; business: PublicBusinessLike }>(
        `/api/drafts/${encodeURIComponent(arrival.draft)}/attach`,
      )
      .then((res) => {
        if (cancelled) return;
        consumeArrival();
        stashAttachedBusiness(res.locationId, res.business);
        // Translate the Location id into the canonical pair before navigating.
        // The lookup proves this caller's membership server-side, so a stale or
        // foreign id lands on `/app` rather than anywhere it should not.
        void resolveLegacyLocationId(res.locationId).then((pair) => {
          if (cancelled || !pair) return;
          writeActiveTenant(pair);
          setAttachedTarget(locationRoutes.overview(pair.businessId, pair.locationId));
        });
        setAttachedSiteId(res.locationId);
      })
      .catch((err) => {
        if (cancelled) return;
        consumeArrival();
        const code = (err as { body?: { error?: string; name?: string } })?.body?.error;
        // T1.5E1 + T1.6B3: an UNCLAIMED draft attaches to nothing, because a
        // draft must never create tenancy — canonical onboarding owns that.
        // The server says `onboarding_required` and returns prefill hints; the
        // visitor goes to /app/start and creates a real tenant there. The
        // draft's content is deliberately NOT carried across: writing it into a
        // tenant would need a "materialize into an existing location"
        // capability that does not exist, and inventing one to finish a legacy
        // attachment is not worth widening the claim surface for (B3 §15's
        // sanctioned handoff).
        if (code === 'onboarding_required') {
          setNeedsOnboarding(true);
          return;
        }
        setAttachError(err instanceof Error ? err.message : 'draft_attach_failed');
      });
    return () => {
      cancelled = true;
    };
    // Run once on mount — `arrival.draft` never changes after the first
    // render (lazy initializer), and re-running this on a later
    // searchParams change would risk a second attach call for the same
    // draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const draftPending = Boolean(arrival.draft) && !attachedSiteId && !attachError && !needsOnboarding;
  const hasError = Boolean(claimError || gbpError || attachError);

  // --- deterministic /app resolution (T1.6A) --------------------------------
  // The server says which businesses this user is a member of and, for each,
  // which locations that membership may actually reach. The ORDER is decided by
  // the pure resolver (lib/active-tenant-resolve.ts); this component only does
  // the I/O around it: the one-time legacy-key migration, then the redirect.
  const [target, setTarget] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (hasError || draftPending || attachedSiteId) return;
    let cancelled = false;
    (async () => {
      // One-time migration of `pb.activeBusiness`, which held a bare SITE id.
      // It is NEVER read as a Business id: the server translates it and proves
      // membership first. The key is retired on every outcome, so a dead value
      // cannot make this run again.
      // Retired unconditionally, before anything can go wrong with it: the key
      // is spent on this visit whether it resolved, was refused, or was junk.
      // That is what makes the migration one-time and loop-proof.
      const legacy = readLegacyActive();
      clearLegacyActive();
      if (legacy && !readActiveTenant()) {
        const pair = await resolveLegacyLocationId(legacy);
        if (pair) writeActiveTenant(pair);
      }

      const discovery = await api.get<DiscoveryResponse>('/api/businesses');
      if (cancelled) return;
      const r = resolveApp(discovery, readActiveTenant());
      switch (r.kind) {
        case 'location':
          // Remember what we just resolved, so the next visit is stable.
          writeActiveTenant({ businessId: r.businessId, locationId: r.locationId });
          setTarget(locationRoutes.overview(r.businessId, r.locationId));
          return;
        case 'resume':
          // A provisional business resumes onboarding; it never becomes the
          // acting tenant, so nothing is written to storage here.
          setTarget(appRoutes.start({ resume: r.businessId }));
          return;
        case 'no-location':
          // An active business with nothing this member may open. Saying so is
          // the honest answer; inventing a location or pretending they have no
          // business would both be worse.
          setNoLocation(true);
          return;
        case 'start':
          setTarget(appRoutes.start());
          return;
      }
    })().catch((err) => {
      if (!cancelled) setError(err instanceof Error ? err.message : String(err));
    });
    return () => {
      cancelled = true;
    };
  }, [hasError, draftPending, attachedSiteId]);

  // The draft had no tenant to attach to: canonical onboarding is the
  // destination, and it is the ONLY thing that creates one. Placed with the
  // other terminal branches — every hook above runs unconditionally.
  if (needsOnboarding) return <Navigate to={appRoutes.start()} replace />;

  if (attachedSiteId) {
    // An ALREADY-claimed draft, whose claiming Business this caller was just
    // proven to own (routes/api/drafts.ts). It is a real, active tenant, so the
    // honest destination is its overview — resolved through the same
    // membership-checked translation every legacy id goes through, never by
    // assuming the returned Location id names a Business.
    return <Navigate to={attachedTarget ?? appRoutes.start()} replace />;
  }

  if (draftPending) {
    return (
      <div className="mx-auto max-w-prose px-6 py-16">
        <p role="status" className="text-sm text-text-secondary">
          We're getting your preview ready.
        </p>
      </div>
    );
  }

  if (claimError) {
    return (
      <div className="mx-auto max-w-prose px-6 py-16">
        <p role="alert" className="text-sm text-danger-fg">
          {claimError}
        </p>
        <Link to="/app" className="mt-4 inline-block text-sm font-medium text-primary">
          Continue to your dashboard →
        </Link>
      </div>
    );
  }

  if (attachError) {
    return (
      <div className="mx-auto max-w-prose px-6 py-16">
        <p role="alert" className="text-sm text-danger-fg">
          {DRAFT_ATTACH_ERROR_MESSAGES[attachError] ?? DRAFT_ATTACH_FALLBACK_MESSAGE}
        </p>
        <Link to="/app" className="mt-4 inline-block text-sm font-medium text-primary">
          Continue to your dashboard →
        </Link>
      </div>
    );
  }

  if (gbpError) {
    return (
      <div className="mx-auto max-w-prose px-6 py-16">
        <div role="alert" className="rounded-lg bg-warning-bg px-4 py-3 text-sm text-warning-fg">
          {gbpError.message}
        </div>
        <Link to="/app" className="mt-4 inline-block text-sm font-medium text-primary">
          Continue to your dashboard →
        </Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-prose px-6 py-16">
        <p role="alert" className="text-sm text-danger-fg">
          Couldn't load your businesses: {error}
        </p>
      </div>
    );
  }

  if (noLocation) {
    return (
      <div className="mx-auto max-w-prose px-6 py-16">
        <p role="status" className="text-sm text-text-secondary">
          You don't have access to a location in this business yet. Ask an owner to add you to one.
        </p>
      </div>
    );
  }

  if (target) return <Navigate to={target} replace />;
  return null; // resolving — no flash of empty content
}
